import { useCallback, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import useDeepCompareEffect from "use-deep-compare-effect";
import { z } from "zod";

import Button from "components/ui/button/Button";
import { toast } from "components/ui/toast/Toast";
import { routes } from "lib/routes";
import * as tracking from "lib/tracking";
import CentredSpinner from "../../lib/core_components/CentredSpinner";
import { useDebouncedValue } from "../../lib/debounce";
import { useFeedPreviewCounts } from "../../lib/hooks/api/signals/useFeedPreviewCounts";
import { useUpdateSignalSettings } from "../../lib/hooks/api/signals/useUpdateSignalSettings";
import {
  SignalSettings as SavedSignalSettings,
  useSignalSettings,
} from "../../lib/hooks/api/teams/useSignalSettings";
import { useSubscription } from "../../lib/hooks/subscription";
import { useUnsavedChangeWarning } from "../../lib/hooks/useUnsavedChangesWarning";
import useWatchFormValues from "../../lib/hooks/useWatchFormValues";
import Signal from "../../lib/icons/Signal";
import { SelfServeQuery } from "../../lib/StotlesApi";
import {
  createSignalSettingsSchema,
  defaultSignalSettings,
  generatePreviewFeedCriteria,
  getDefaultSignalSettings,
  parseSignalSettings,
  SignalSettingsForm as SignalSettingsFormValues,
  updatePreviewFeedCriteria,
} from "../../lib/utils/signalSettingsUtils";
import Collapsible from "../ui/collapsible/Collapsible";
import { FeedSettingsPreview } from "./feed_settings/FeedSettingsPreview";
import CountryInput from "./signal_settings/country_input/CountryInput";
import CPVCodeInput from "./signal_settings/CPVCodeInput";
import ExportSignalSettingsButton from "./signal_settings/ExportSignalSettingsButton";
import KeywordInput from "./signal_settings/keyword_input/KeywordInput";
import SignalSettingsConfirmationModal from "./signal_settings/SignalSettingsConfirmationModal";
import SignalSettingsDiscardModal from "./signal_settings/SignalSettingsDiscardModal";
import SupplierInput from "./signal_settings/supplier_input/SupplierInput";

import css from "./SignalSettings.module.scss";

type SignalSettingsProps = {
  internationalDataAccess: boolean;
};

export default function SignalSettings({ internationalDataAccess }: SignalSettingsProps) {
  const { data: signals, isLoading: isLoadingSignals } = useSignalSettings();

  if (isLoadingSignals) {
    return (
      <div className={css.loadingContainer}>
        <CentredSpinner />
      </div>
    );
  }

  let signalSettings = defaultSignalSettings;
  if (signals) {
    signalSettings = getDefaultSignalSettings(signals);
  }
  return (
    <SignalSettingsForm
      internationalDataAccess={internationalDataAccess}
      signals={signalSettings}
      savedSignals={signals}
    />
  );
}

function SignalSettingsForm({
  internationalDataAccess,
  signals,
  savedSignals,
}: SignalSettingsProps & {
  signals: SignalSettingsFormValues;
  savedSignals: SavedSignalSettings | undefined;
}) {
  const navigate = useNavigate();
  const subscription = useSubscription();
  const {
    mutate: updateSignalSettings,
    isSuccess: isSuccessUpdateSignalSettings,
    reset: resetUpdateSignalSettings,
  } = useUpdateSignalSettings({
    onSuccess: (_) => {
      tracking.logEvent(tracking.EventNames.recordQueryUpdated, {});
      toast.success("Signal settings updated successfully");
    },
    onError: (_) => {
      toast.error("Failed to update signal settings");
    },
  });

  const signalSettingsSchema = createSignalSettingsSchema(internationalDataAccess);

  const [previewFeedCriteria, setPreviewFeedCriteria] = useState<SelfServeQuery>(
    generatePreviewFeedCriteria(),
  );
  // Both the per-signal counts and the notice-list preview are served by fast
  // app-service queries now, so a single short debounce keeps them responsive.
  const [debouncedPreviewCriteria] = useDebouncedValue(previewFeedCriteria, 500);

  // One fetch, split into a per-category map so each input looks up by its own
  // raw value (keyword string / org id / CPV code) with no key collisions.
  const { keywordCounts, competitorCounts, partnerCounts, cpvCounts, isLoadingCounts } =
    useFeedPreviewCounts({
      keywords: debouncedPreviewCriteria.keywords?.keywords ?? [],
      competitorIds: debouncedPreviewCriteria.competitors?.org_ids ?? [],
      partnerIds: debouncedPreviewCriteria.partners?.org_ids ?? [],
      cpvCodes: debouncedPreviewCriteria.cpv_codes?.codes ?? [],
      countries: debouncedPreviewCriteria.countries ?? [],
    });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const {
    control,
    getValues,
    handleSubmit,
    reset: resetForm,
    formState: { isDirty },
  } = useForm<z.input<typeof signalSettingsSchema>, unknown, z.output<typeof signalSettingsSchema>>(
    {
      resolver: zodResolver(signalSettingsSchema),
      defaultValues: signals,
    },
  );

  // Prompt user to confirm if they have unsaved changes
  useUnsavedChangeWarning(isDirty);

  // Watch for changes to the form values and update the preview feed criteria
  const handleFormValuesChange = useCallback(
    (values: SignalSettingsFormValues) => {
      updatePreviewFeedCriteria(
        values,
        {
          hasAwards: subscription.hasDataTypes("AWARDS"),
          hasBuyers: subscription.hasDataTypes("BUYERS"),
          hasSuppliers: subscription.hasDataTypes("SUPPLIERS"),
        },
        setPreviewFeedCriteria,
      );
    },
    [subscription],
  );
  useWatchFormValues(control, handleFormValuesChange);

  useDeepCompareEffect(() => {
    if (signals) {
      resetForm(signals);
      handleFormValuesChange(getValues());
    }
  }, [signals]);

  const handleModalSubmit = () => {
    const { countries, languages, keywords, competitors, partners, cpvCodes } = getValues();
    const parsedSignalSettings = parseSignalSettings({
      countries,
      languages,
      keywords,
      competitors,
      partners,
      cpvCodes,
    });
    updateSignalSettings(parsedSignalSettings);
  };

  const handleConfirmationRedirect = () => {
    void navigate({ to: routes.notices });
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  const handleDiscardModalOpen = () => {
    setIsDiscardModalOpen(true);
  };

  const handleDiscardModalCancel = () => {
    setIsDiscardModalOpen(false);
  };

  const handleDiscardModalSubmit = () => {
    resetForm();
    handleDiscardModalCancel();
  };

  const onSubmit: SubmitHandler<SignalSettingsFormValues> = () => {
    setIsModalOpen(true);
  };

  const ActionButtons = () => {
    return (
      <>
        <Button buttonType="secondary" onClick={handleDiscardModalOpen} disabled={!isDirty}>
          Discard Changes
        </Button>
        <Button type="submit" disabled={!isDirty}>
          Save
        </Button>
      </>
    );
  };

  return (
    <form className={css.container} onSubmit={handleSubmit(onSubmit)}>
      <div className={css.header}>
        <div className={css.headerContainer}>
          <div className={css.headerTitle}>
            <Signal size={24} />
            <p className={css.title}>Signal Settings</p>
          </div>
          <div className={css.headerActions}>
            <ExportSignalSettingsButton savedSignals={savedSignals} hasUnsavedChanges={isDirty} />
            <ActionButtons />
          </div>
        </div>
        <p className={css.subtitle}>
          We use signals to provide more relevant data for you in the app. Customise your signals to
          improve your signal score and find the most relevant information.
        </p>
      </div>
      <div className={css.content}>
        {/* Signal Settings */}
        <div className={css.signalSettingsContainer}>
          <Collapsible
            title="Which countries would you like to cover?"
            subtitle="Find notices from buyers based in the following countries."
            defaultOpen
            isBordered
          >
            <CountryInput
              countryFieldName="countries"
              languageFieldName="languages"
              control={control}
              internationalDataAccess={internationalDataAccess}
            />
          </Collapsible>

          <Collapsible
            title="Track keywords"
            subtitle="Find notices about your products & services by tracking mentions of related keywords."
            defaultOpen
            isBordered
          >
            <KeywordInput
              name="keywords"
              control={control}
              resultCounts={keywordCounts}
              isLoadingCounts={isLoadingCounts}
            />
          </Collapsible>

          <Collapsible
            title="Track Suppliers"
            subtitle="Save suppliers as your competitors & partners to track their activity and find upcoming renewals."
            defaultOpen
            isBordered
          >
            <SupplierInput
              competitorFieldName="competitors"
              partnerFieldName="partners"
              control={control}
              competitorCounts={competitorCounts}
              partnerCounts={partnerCounts}
              isLoadingCounts={isLoadingCounts}
            />
          </Collapsible>

          <Collapsible
            title="Track CPV codes"
            subtitle="Save CPV codes that are most relevant to your business to source opportunities."
            defaultOpen
            isBordered
          >
            <CPVCodeInput
              name="cpvCodes"
              control={control}
              resultCounts={cpvCounts}
              isLoadingCounts={isLoadingCounts}
            />
          </Collapsible>
        </div>

        <div className={css.noticesContainer}>
          <FeedSettingsPreview feedCriteria={debouncedPreviewCriteria} />
        </div>
      </div>
      {/* Footer */}
      <div className={css.footer}>
        <ActionButtons />
      </div>
      {/* Modals */}
      <SignalSettingsConfirmationModal
        isOpen={isModalOpen}
        showConfirmation={isSuccessUpdateSignalSettings}
        onSubmit={handleModalSubmit}
        onCancel={handleModalCancel}
        onConfirmationRedirect={handleConfirmationRedirect}
        resetUpdateSignalSettings={resetUpdateSignalSettings}
      />
      <SignalSettingsDiscardModal
        isOpen={isDiscardModalOpen}
        onCancel={handleDiscardModalCancel}
        onSubmit={handleDiscardModalSubmit}
      />
    </form>
  );
}
